export type EnvVarSource = 'server' | 'public';

type EnvFailure = {
  name: string;
  reason?: string;
  source: EnvVarSource;
  requiredBy: string;
};

type Collector = {
  add: (failure: EnvFailure) => void;
  flush: () => EnvFailure[];
};

const globalKey = '__ENV_ERROR_COLLECTOR__';

const getCollector = (): Collector => {
  const g = globalThis as unknown as Record<string, unknown>;
  const existing = g[globalKey] as Collector | undefined;
  if (existing) return existing;

  let failures: EnvFailure[] = [];

  const add = (failure: EnvFailure) => {
    failures = [...failures, failure];
  };

  const flush = () => {
    const copy = failures;
    failures = [];
    return copy;
  };

  const c: Collector = { add, flush };
  g[globalKey] = c;
  return c;
};

const isEnvFileFrame = (line: string) =>
  line.includes('/src/lib/env') ||
  line.includes('env.server') ||
  line.includes('env.public') ||
  line.includes('env.ts') ||
  line.includes('envCollector');

const firstExternalFrame = (stack: string) => {
  const lines = stack.split('\n').map(l => l.trim());
  const frame = lines.find(l => l.startsWith('at ') && !isEnvFileFrame(l));
  return frame ?? lines[1] ?? '(unknown)';
};

export const recordEnvFailure = (
  name: string,
  reason: string | undefined,
  source: EnvVarSource
) => {
  const err = new Error();
  const stack = typeof err.stack === 'string' ? err.stack : '';
  const requiredBy = firstExternalFrame(stack);
  getCollector().add({ name, reason, requiredBy, source });
};

export const finalizeEnvValidation = () => {
  const failures = getCollector().flush();
  if (failures.length === 0) return;

  const header = `Environment validation failed with ${failures.length} issue(s).`;

  const humanReadable = [
    header,
    '',
    ...failures.map((f, i) =>
      [
        `${i + 1}. [${f.source}] ${f.name}${f.reason ? ` — ${f.reason}` : ''}`,
        `    required from: ${f.requiredBy}`,
      ].join('\n')
    ),
  ].join('\n');

  console.error(humanReadable);

  // Skip throwing errors during Fly build, similar to next.config.ts logic
  if (process.env.FLY_BUILD === 'true') {
    console.warn(
      'Skipping environment validation error throw during Fly build'
    );
    return;
  }

  throw new Error(header);
};
